import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  useEditMode,
  EditableToken,
} from '@vibesites/runtime/react';
import { getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Helpers — normalize YouTube / Vimeo / direct video URLs
// ============================================================================

function youtubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/embed/')) {
        return `https://www.youtube.com${u.pathname}${u.search}`;
      }
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      const parts = u.pathname.split('/').filter(Boolean);
      if ((parts[0] === 'shorts' || parts[0] === 'live') && parts[1]) {
        return `https://www.youtube.com/embed/${parts[1]}`;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function vimeoEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const parts = u.pathname.split('/').filter(Boolean);
      const id = parts[0] === 'video' ? parts[1] : parts[0];
      return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function isDirectVideoUrl(url: string): boolean {
  if (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(url)) return true;
  try {
    const host = new URL(url).hostname;
    return (
      host.includes('media.multihub') ||
      host.includes('multihubmedia') ||
      host.includes('vid.cdn-website')
    );
  } catch {
    return false;
  }
}

function resolvePoster(videoUrl: string | null | undefined): string | undefined {
  if (!videoUrl) return undefined;
  const key = parseStorageKey(videoUrl);
  const thumbKey = videoThumbStorageKey(key);
  return thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : undefined;
}

type Playback =
  | { kind: 'iframe'; src: string }
  | { kind: 'video'; src: string; poster?: string }
  | null;

function resolvePlayback(embedUrl: string | null, videoUrl: string | null): Playback {
  const embed = embedUrl?.trim() || '';
  const video = videoUrl?.trim() || '';
  const candidate = embed || video;
  if (!candidate) return null;

  const yt = youtubeEmbedUrl(candidate);
  if (yt) return { kind: 'iframe', src: yt };

  const vim = vimeoEmbedUrl(candidate);
  if (vim) return { kind: 'iframe', src: vim };

  if (isDirectVideoUrl(candidate) || candidate === video) {
    return { kind: 'video', src: candidate, poster: resolvePoster(candidate) };
  }

  return { kind: 'iframe', src: candidate };
}

// ============================================================================
// Component
// ============================================================================

export function CommunityFilmEmbed() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const showPlaceholders = Boolean(editMode?.isEditMode);

  const videoUrl = useContentToken('communityfilmembed.video');
  const embedUrl = useContentToken('communityfilmembed.embed_url');
  const title = useContentToken('communityfilmembed.title');
  const a11yTitle = title?.trim() || 'Community film';

  const playback = resolvePlayback(embedUrl, videoUrl);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up relative overflow-hidden bg-[var(--vibe-color-background)] py-10 md:py-14"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        aria-hidden
        style={{
          backgroundImage:
            'radial-gradient(ellipse 70% 55% at 50% 0%, color-mix(in srgb, var(--vibe-color-primary) 14%, transparent), transparent 60%),' +
            'linear-gradient(180deg, color-mix(in srgb, var(--vibe-color-text) 6%, transparent), transparent 40%)',
        }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-0 sm:px-4 md:px-6 lg:px-8">
        <EditableToken
          path="communityfilmembed.video"
          type="video"
          label="Community Film Video"
          as="div"
          className="block w-full"
        >
          <EditableToken
            path="communityfilmembed.embed_url"
            type="url"
            label="Embed URL"
            as="div"
            className="block w-full"
          >
            <EditableToken
              path="communityfilmembed.title"
              type="text"
              label="Video Title"
              as="div"
              className="block w-full"
            >
              {playback?.kind === 'iframe' ? (
                <div className="relative w-full overflow-hidden bg-black aspect-video shadow-[0_24px_60px_-28px_rgba(0,0,0,0.45)]">
                  <iframe
                    src={playback.src}
                    title={a11yTitle}
                    className="absolute inset-0 h-full w-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              ) : playback?.kind === 'video' ? (
                <div className="relative w-full overflow-hidden bg-black aspect-video shadow-[0_24px_60px_-28px_rgba(0,0,0,0.45)]">
                  <video
                    src={playback.src}
                    poster={playback.poster}
                    controls
                    playsInline
                    preload="metadata"
                    title={a11yTitle}
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex aspect-video w-full items-center justify-center bg-[var(--vibe-color-surface)] px-6 text-center">
                  <p className="m-0 font-body text-base text-[var(--vibe-color-text-muted)] md:text-lg">
                    {showPlaceholders
                      ? 'Add a community film — upload a video or paste a YouTube / Vimeo URL.'
                      : 'Community film coming soon.'}
                  </p>
                </div>
              )}
            </EditableToken>
          </EditableToken>
        </EditableToken>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunityFilmEmbed',
  displayName: 'Community Film',
  tokens: {
    'communityfilmembed.video': {
      label: 'Community Film Video',
      type: 'video',
      defaultField: 'property_featured_video',
      defaultValue: '',
    },
    'communityfilmembed.embed_url': {
      label: 'Embed URL (YouTube / Vimeo)',
      type: 'url',
      defaultValue: '',
      description:
        'Optional. Paste a YouTube or Vimeo link to override the featured video. Leave empty to use the uploaded community film.',
    },
    'communityfilmembed.title': {
      label: 'Video Title',
      type: 'text',
      defaultValue: 'Community Film',
      description: 'Accessible title for the video player.',
    },
  },
};

(CommunityFilmEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default CommunityFilmEmbed;
